import React, { useEffect } from 'react';
import { instanceOf, number, string } from 'prop-types';
import moment from 'moment';
import { useFormik } from 'formik';

import DateField from '../FormFields/DateField';
import validationSchema from './TimestampFilter.schema';

const TimestampFilter = ({
    dateFormat,
    endTimestamp,
    endTimestampDisabled = false,
    maxDate,
    minDate,
    onFilterChange,
    showTimeSelect,
    startTimestamp,
    startTimestampDisabled = false,
    timeFormat,
    timeIntervals
}) => {
    const { errors, touched, values, ...form } = useFormik({
        enableReinitialize: true,
        initialTouched:     {
            endTimestamp:   true,
            startTimestamp: true
        },
        initialValues: {
            endTimestamp:   endTimestamp,
            startTimestamp: startTimestamp
        },
        validateOnBlur: false,
        validationSchema
    });

    useEffect(() => {
        form.validateForm();
    }, [endTimestamp, startTimestamp]);

    /**
     * Modifies the appropriate timestamp value based on the value of the
     * changed requisite date or time input.
     */
    function handleChange(name) {
        return (value, event) => {
            let parsedValue = null;
            if (value) {
                parsedValue = moment(value);
                form.setFieldValue(name, parsedValue);
            }
            form.setFieldTouched(name, true);
            onFilterChange(name, parsedValue, value);
        };
    }

    return (
        <>
            <DateField
                dateFormat={dateFormat}
                isClearable={!startTimestampDisabled}
                label="Start Time"
                margin="dense"
                maxDate={values.endTimestamp.toDate()}
                maxTime={values.endTimestamp.toDate()}
                minTime={values.endTimestamp.clone().startOf('day').toDate()}
                minDate={minDate ? new Date(minDate) : ''}
                name="startTimestamp"
                onChange={handleChange('startTimestamp')}
                showTimeSelect={showTimeSelect}
                timeFormat={timeFormat}
                timeIntervals={timeIntervals}
                value={values.startTimestamp.toDate()}
                disabled={startTimestampDisabled}
            />
            <DateField
                dateFormat={dateFormat}
                errors={errors.endTimestamp}
                isClearable={!endTimestampDisabled}
                label="End Time"
                margin="dense"
                maxTime={maxDate ? new Date(maxDate) : values.endTimestamp.clone().endOf('day').toDate()}
                minDate={values.startTimestamp.toDate()}
                minTime={values.startTimestamp.toDate()}
                maxDate={maxDate ? new Date(maxDate) : ''}
                name="endTimestamp"
                onChange={handleChange('endTimestamp')}
                showTimeSelect={showTimeSelect}
                timeFormat={timeFormat}
                timeIntervals={timeIntervals}
                touched={touched.endTimestamp}
                value={values.endTimestamp.toDate()}
                disabled={endTimestampDisabled}
            />
        </>
    );
};

TimestampFilter.defaultProps = {
    dateFormat:     'yyyy-MM-dd h:mm a',
    showTimeSelect: true,
    timeFormat:     'h:mm a',
    timeIntervals:  15
};

TimestampFilter.propTypes = {
    dateFormat:     string,
    endTimestamp:   instanceOf(moment).isRequired,
    startTimestamp: instanceOf(moment).isRequired,
    timeFormat:     string,
    timeIntervals:  number
};

export default TimestampFilter;
